import { Typography } from 'web-library';
import React from 'react';

import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';
import { ConfigItem, ControlType } from './ConfigItem';
import { ConfigKeys, Configs } from '../../../models';

const useStyles = makeStyles()((theme) => ({
  errMsg: {
    color: theme.color.state.stateError,
  },
}));

export interface AdminConfigurationItemsProps {
  formHook: UseFormReturn;
  resultData: Configs;
}

const AdminConfigurationItems = ({ formHook, resultData }: AdminConfigurationItemsProps) => {
  const { t } = useTranslation();

  const { classes } = useStyles();

  const {
    formState: { errors },
  } = formHook;

  const getConfigValue = (key: ConfigKeys) => (resultData ? resultData[key].value : '');

  return (
    <>
      <ConfigItem
        label={t('admin->>AdminConfiguration.Disable account')}
        type={ControlType.number}
        description={t(
          'admin->>AdminConfiguration.Disable the account x days after Results Release'
        )}
        ariaLabel="fas"
        defaultValue={getConfigValue('daysToDisableAccount')}
        name="daysToDisableAccount"
        form={formHook}
        rules={{ min: 1, max: 365 }}
        hidden
        isError={!!errors.daysToDisableAccount}
      />
      {/* commented since its unused}
      {/*{errors.daysToDisableAccount && (*/}
      {/*  <Typography align="right" className={classes.errMsg}>*/}
      {/*    Configuration value should be larger than 0 and less than 366.*/}
      {/*  </Typography>*/}
      {/*)}*/}
      <ConfigItem
        label={t('admin->>AdminConfiguration.Invalidate account')}
        type={ControlType.number}
        description={t('admin->>AdminConfiguration.InvalidateAccountAfterXDays')}
        defaultValue={getConfigValue('daysToLockInactivityAccount')}
        name="daysToLockInactivityAccount"
        form={formHook}
        rules={{ min: 1, max: 365 }}
        isError={!!errors.daysToLockInactivityAccount}
      />
      {errors.daysToLockInactivityAccount && (
        <Typography align="right" className={classes.errMsg}>
          Configuration value should be larger than 0 and less than 366.
        </Typography>
      )}
      <ConfigItem
        label={t('admin->>AdminConfiguration.Delete account reminder')}
        type={ControlType.number}
        description={t(
          'admin->>AdminConfiguration.To email Admin x days after accounts disabled for Admin to view the accounts to be deleted'
        )}
        defaultValue={getConfigValue('daysToEmailAdmin')}
        name="daysToEmailAdmin"
        form={formHook}
        rules={{ min: 1, max: 365 }}
        isError={!!errors.daysToEmailAdmin}
        hidden
      />
      {/* commented since its unused}
      {/*{errors.daysToEmailAdmin && (*/}
      {/*  <Typography align="right" className={classes.errMsg}>*/}
      {/*    Configuration value should be larger than 0 and less than 366.*/}
      {/*  </Typography>*/}
      {/*)}*/}
      <ConfigItem
        label={t('admin->>AdminConfiguration.Result release link expiry')}
        type={ControlType.number}
        description={t(
          'admin->>AdminConfiguration.x days for Results Release Unique Link before expiry after generating it for each candidate'
        )}
        defaultValue={getConfigValue('daysToInvalidateLink')}
        name="daysToInvalidateLink"
        form={formHook}
        rules={{ min: 1, max: 365 }}
        isError={!!errors.daysToInvalidateLink}
        hidden
      />
      {/* commented since its unused}
      {/* {errors.daysToInvalidateLink && (*/}
      {/*  <Typography align="right" className={classes.errMsg}>*/}
      {/*    Configuration value should be larger than 0 and less than 366.*/}
      {/*  </Typography>*/}
      {/*)}*/}
      <ConfigItem
        label={t('admin->>AdminConfiguration.Login attempt numbers')}
        type={ControlType.number}
        description={t('admin->>AdminConfiguration.Max number of login attempts')}
        defaultValue={getConfigValue('timesOfMaxLogin')}
        name="timesOfMaxLogin"
        form={formHook}
        isError={!!errors.timesOfMaxLogin}
        rules={{ min: 1, max: 10 }}
      />
      {errors.timesOfMaxLogin && (
        <Typography align="right" className={classes.errMsg}>
          Configuration value should be larger than 0 and less than 11.
        </Typography>
      )}
      <ConfigItem
        label={t('admin->>AdminConfiguration.Privileged admin accounts')}
        type={ControlType.number}
        description={t('admin->>AdminConfiguration.x minutes of inactivity before session timeout')}
        defaultValue={getConfigValue('minutesOfSessionTimeoutPrivilegedAdmin')}
        name="minutesOfSessionTimeoutPrivilegedAdmin"
        form={formHook}
        isError={!!errors.minutesOfSessionTimeoutPrivilegedAdmin}
        rules={{ min: 1, max: 30 }}
      />
      {errors.minutesOfSessionTimeoutPrivilegedAdmin && (
        <Typography align="right" className={classes.errMsg}>
          Configuration value should be larger than 0 and less than 31.
        </Typography>
      )}
      <ConfigItem
        label={t('admin->>AdminConfiguration.Non-Privileged admin accounts')}
        type={ControlType.number}
        description={t('admin->>AdminConfiguration.x minutes of inactivity before session timeout')}
        defaultValue={getConfigValue('minutesOfSessionTimeoutNonPriviledgeAdmin')}
        name="minutesOfSessionTimeoutNonPriviledgeAdmin"
        form={formHook}
        isError={!!errors.minutesOfSessionTimeoutNonPriviledgeAdmin}
        rules={{ min: 1, max: 600 }}
      />
      {errors.minutesOfSessionTimeoutNonPriviledgeAdmin && (
        <Typography align="right" className={classes.errMsg}>
          Configuration value should be larger than 0 and less than 601.
        </Typography>
      )}
      <ConfigItem
        label={t('admin->>AdminConfiguration.Candidates and Coordinators accounts')}
        type={ControlType.number}
        description={t('admin->>AdminConfiguration.x minutes of inactivity before session timeout')}
        defaultValue={getConfigValue('minutesOfSessionTimeout')}
        name="minutesOfSessionTimeout"
        form={formHook}
        isError={!!errors.minutesOfSessionTimeout}
        rules={{ min: 1, max: 600 }}
      />
      {errors.minutesOfSessionTimeout && (
        <Typography align="right" className={classes.errMsg}>
          Configuration value should be larger than 0 and less than 601.
        </Typography>
      )}{' '}
      <ConfigItem
        label={t('admin->>AdminConfiguration.Expire payment link')}
        type={ControlType.number}
        description={t('admin->>AdminConfiguration.x days before payment unique link expires')}
        defaultValue={getConfigValue('daysOfPaymentLinkExpire')}
        name="daysOfPaymentLinkExpire"
        form={formHook}
        isError={!!errors.daysOfPaymentLinkExpire}
        rules={{ min: 1, max: 365 }}
      />
      {errors.daysOfPaymentLinkExpire && (
        <Typography align="right" className={classes.errMsg}>
          Configuration value should be larger than 0 and less than 366.
        </Typography>
      )}
      <ConfigItem
        label={t('admin->>AdminConfiguration.Expire access link')}
        type={ControlType.number}
        description={t(
          'admin->>AdminConfiguration.x days before Exam Registration/Mother Tongue Resit/Appeal link expiry sent by Ops Admin'
        )}
        defaultValue={getConfigValue('daysOfAccessLinkExpire')}
        name="daysOfAccessLinkExpire"
        form={formHook}
        isError={!!errors.daysOfAccessLinkExpire}
        rules={{ min: 1, max: 365 }}
      />
      {errors.daysOfAccessLinkExpire && (
        <Typography align="right" className={classes.errMsg}>
          Configuration value should be larger than 0 and less than 366.
        </Typography>
      )}
      <ConfigItem
        label={t('admin->>AdminConfiguration.Failed attempt numbers')}
        type={ControlType.number}
        description={t(
          'admin->>AdminConfiguration.Max number of failed attempts to enable CAPTCHA'
        )}
        defaultValue={getConfigValue('timesOfMaxFailedAttempt')}
        name="timesOfMaxFailedAttempt"
        form={formHook}
        isError={!!errors.timesOfMaxFailedAttempt}
        rules={{ min: 1, max: 10 }}
      />
      {errors.timesOfMaxFailedAttempt && (
        <Typography align="right" className={classes.errMsg}>
          Configuration value should be larger than 0 and less than 11.
        </Typography>
      )}
      <ConfigItem
        label={t('admin->>AdminConfiguration.Login retry seconds')}
        type={ControlType.number}
        description={t('admin->>AdminConfiguration.Max seconds (randomised) to retry CAPTCHA')}
        defaultValue={getConfigValue('secondToWaitLogin')}
        name="secondToWaitLogin"
        form={formHook}
        isError={!!errors.secondToWaitLogin}
        rules={{ min: 1, max: 3000 }}
      />
      {errors.secondToWaitLogin && (
        <Typography align="right" className={classes.errMsg}>
          Configuration value should be larger than 0 and less than 3001.
        </Typography>
      )}
    </>
  );
};

export default AdminConfigurationItems;
